import { KartoyIcon } from '@mobile/components/kartoy/kartoy-icon'
import { PillButton } from '@mobile/components/kartoy/pill-button'
import { ServiceIconStack } from '@mobile/components/kartoy/service-icon-stack'
import type { RootRouteProps } from '@mobile/screens'
import type { CardTemplateId } from '@shared-core/features/backend'
import { useAuthorizationGate } from '@shared-core/hooks/use-authorization-gate'
import { useCardTemplates } from '@shared-core/hooks/use-card-templates'
import { useI18N } from '@shared-core/hooks/use-i18n'
import { ScreensList, useGoFromSheet, useNavigation, useRoute } from '@shared-core/navigation'
import { formatWithThousandSeparators } from '@shared-core/utils/balances/balances'
import { AppBankCard } from '@shared-ui/components/app/bank-card'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { Gradient } from '@shared-ui/components/ui/gradient'
import { GText } from '@shared-ui/components/ui/text'
import emailLocales from '@shared-ui/form-sheets/email-prompt/index.i18n.json'
import { useAppTheme } from '@shared-ui/theme'
import { Pressable, View } from 'react-native'
import Animated, { Easing, FadeIn, FadeInDown } from 'react-native-reanimated'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

export function ScreenCardInfo({ embedded = false, cardType }: { embedded?: boolean; cardType?: CardTemplateId } = {}) {
  const styles = useCreateStyles()
  const theme = useAppTheme()
  const { t } = useI18N(locales)
  const { t: emailText } = useI18N(emailLocales)
  const navigation = useNavigation()
  const route = useRoute<RootRouteProps<ScreensList.CARD_INFO>>()
  const templateId = cardType ?? route.params?.templateId ?? 'virtual'
  const templates = useCardTemplates()
  const template = templates.find((item) => item.id === templateId)
  const regularTemplate = templates.find((item) => item.id === 'virtual')
  const formatRate = (value?: string) => (value ? formatWithThousandSeparators(value) : '—')
  const goFromSheet = useGoFromSheet()
  const { hasPhone, hasEmail, ensureAuthorized } = useAuthorizationGate({ fromSheet: !embedded, requireEmail: true })
  const reveal = (delay: number) =>
    FadeInDown.delay(delay)
      .duration(520)
      .easing(Easing.out(Easing.cubic))
      .withInitialValues({ opacity: 0, transform: [{ translateY: 14 }] })

  const handlePhone = () => {
    ensureAuthorized(true)
  }

  const handleIssue = () => {
    if (hasPhone && !hasEmail) {
      if (embedded) navigation.navigate(ScreensList.ENTRY_EMAIL)
      else goFromSheet(ScreensList.ENTRY_EMAIL)
      return
    }

    if (!ensureAuthorized(true) || !template) return

    const params = { templateId }

    if (embedded) navigation.navigate(ScreensList.PAYMENT_LINK, params)
    else goFromSheet(ScreensList.PAYMENT_LINK, params)
  }

  const issueTitle = t('issue-action', {
    price: template ? formatWithThousandSeparators(template.issuePrice.replace(/\.00$/, '')) : '—',
  })

  // Общая плашка курса для шита и полного экрана.
  const rateContent =
    templateId === 'platinum' ? (
      <View style={styles.platinumRate}>
        <Gradient
          colors={[theme.COLOR_NEUTRAL_900, theme.COLOR_NEUTRAL_700, theme.COLOR_NEUTRAL_900]}
          locations={[0.0369, 0.4668, 0.8618]}
          start={{ x: 0.53, y: 0 }}
          end={{ x: 0.47, y: 1 }}
          style={styles.platinumRateBackground}
          pointerEvents="none"
        />
        <GText fw={600} numberOfLines={1} style={styles.platinumRateValue}>
          {t('rate-value-platinum', { rate: formatRate(template?.rates.topupRate) })}
        </GText>
        <GText numberOfLines={1} style={styles.regularRateValue}>
          {t('rate-value-regular', { rate: formatRate(regularTemplate?.rates.topupRate) })}
        </GText>
      </View>
    ) : (
      <GText fw={500} style={styles.rateValue}>
        {t('rate-value', { rate: formatRate(template?.rates.topupRate) })}
      </GText>
    )

  let actionTitle = t('action')

  if (hasPhone) actionTitle = emailText('action')

  if (hasPhone && hasEmail) actionTitle = issueTitle

  return (
    <View style={embedded ? styles.embedded : styles.container}>
      {!embedded && (
        <Animated.View entering={FadeIn.delay(260).duration(360)} style={styles.close}>
          <Pressable accessibilityRole="button" onPress={() => navigation.goBack()}>
            <AppIcon icon="close" size={24} />
          </Pressable>
        </Animated.View>
      )}

      <View style={styles.content}>
        {!embedded && (
          <Animated.View entering={reveal(40)}>
            <AppBankCard type={templateId === 'platinum' ? 'platinum' : 'virtual'} size="middle" />
          </Animated.View>
        )}

        <Animated.View entering={embedded ? undefined : reveal(120)} style={styles.titleBlock}>
          <GText fw={600} style={styles.title}>
            {template?.title ?? t(templateId === 'platinum' ? 'title-platinum' : 'title')}
          </GText>
          <GText style={styles.description}>
            {t(templateId === 'platinum' ? 'description-platinum' : 'description')}
          </GText>
        </Animated.View>

        <Animated.View entering={embedded ? undefined : reveal(200)} style={styles.features}>
          <View style={styles.featureRow}>
            <KartoyIcon icon="goPay" size={20} />
            <View style={styles.featureTextRow}>
              <GText fw={500} style={styles.featureText}>
                {t('rate-label')}
              </GText>
              {rateContent}
            </View>
          </View>

          <View style={styles.featureRow}>
            <ServiceIconStack />
            <GText fw={500} style={styles.featureText}>
              {t('services')}
            </GText>
          </View>

          <Pressable
            accessibilityRole="button"
            style={styles.featureRow}
            onPress={() => navigation.navigate(ScreensList.FORM_SHEET_WALLET_PAY_GUIDE)}
          >
            <View style={styles.payIcons}>
              <KartoyIcon icon="google" size={20} />
              <KartoyIcon icon="apple" size={20} />
            </View>
            <GText fw={500} style={styles.featureText}>
              {t('pay-binding')}
            </GText>
            <AppIcon icon="chevron-right" size={20} isSecondary />
          </Pressable>
        </Animated.View>
      </View>

      <Animated.View entering={embedded ? undefined : reveal(300)}>
        <PillButton
          title={actionTitle}
          disabled={hasPhone && hasEmail && !template}
          icon={hasPhone && hasEmail ? 'goPay' : undefined}
          onPress={hasPhone ? handleIssue : handlePhone}
        />
      </Animated.View>
    </View>
  )
}
